import {
  getSensorAlarms,
  getSensorSessions,
  getSensorConnectivity,
  getPCOStateChanges,
} from "./unitEventsUtils";
import { parseUtc } from "../../services/parseUtc";
import type { TenureScope } from "../../services/installationTenure";

/**
 * The one place telemetry is clipped to the sensor's current location.
 *
 * WHY A WRAPPER AND NOT THE FETCHERS THEMSELVES: the same fetcher serves both
 * questions. `getSensorConnectivity(name, 1)` is the online/offline badge --
 * "what is it doing right now" -- while `getSensorConnectivity(name, 500)` is
 * the report's outage history. `getSensorSessions(name, 1, ...)` is the wellness
 * "last seen" timestamp; `getSensorSessions(name, 1000, ...)` is the trend
 * chart. No rule inside a fetcher can tell those apart -- and the sensors table
 * reads connectivity with the table's page size while using only row [0], so
 * page size is not the tell either. Clipping inside the fetchers would truncate
 * live state and make a sensor moved an hour ago read offline and of unknown
 * firmware, which understates real safety risk.
 *
 * A wrapper also keeps the clip VISIBLE to the hook tests: those mock the data
 * layer wholesale, so a filter hidden inside the fetchers would be untested by
 * construction.
 *
 * THE RULE, applied by `scopedTelemetry.test.ts` rather than trusted:
 *   - a query is SCOPED if its rows are aggregated over time -- counts,
 *     durations, buckets, trends, report periods.
 *   - a query is EXEMPT if only its newest row is read to answer "right now".
 *     Exempt call sites carry a `// LIVE-STATE EXEMPT: <reason>` marker.
 *
 * Clipping runs AFTER the dev-mock injection inside the fetchers
 * (`mockWellnessPresence`, `mockWellnessLastSession`)
 * -- which is the order we want, because all three synthesise rows stamped at
 * roughly now, so they land inside any tenure and survive the clip.
 */

export type ScopedEnvelope<T> = {
  /** Rows at or after the tenure cutoff. Every row when the cutoff is unknown. */
  data: T[];
  /**
   * Every row the fetch returned, unclipped.
   *
   * For LIVE-STATE questions only -- read it through `liveRows` -- and for the
   * location-history breakdown, which is about the other locations by
   * definition. Never aggregate it into a figure describing "this sensor",
   * because that is the misattribution this module exists to stop.
   */
  unclipped: T[];
  scope: TenureScope;
  nextPageToken?: string;
  size?: number;
};

type PagedResponse<T> = {
  data?: T[];
  nextPageToken?: string;
  size?: number;
};

/**
 * Which instant a telemetry row happened at, for the purpose of the cutoff.
 *
 * `dateCreated` FIRST, deliberately. The cutoff is a server-stamped UTC value
 * (`DateTime.UtcNow` when the installation was recorded) and `dateCreated` is
 * the server's stamp for the telemetry row, so both sides of the comparison sit
 * on one clock. This is the same reasoning already written at
 * `useBuildingAggregates` where it prefers `dateCreated` over the event's own
 * timestamp: a sensor whose clock is wrong still arrives at a known moment.
 * Comparing the device's clock against a server cutoff would hand a sensor with
 * an unset RTC either all of its old history or none of its new history,
 * depending on which way the skew ran.
 *
 * The fallbacks exist because `dateCreated` postdates the ingest-stamp rollout
 * and older rows lack it. `Datetime` is the capitalised spelling some rows use.
 */
export function rowTimestampMs(row: unknown): number | null {
  if (row === null || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  return (
    parseUtc(r.dateCreated) ??
    parseUtc(r.datetime) ??
    parseUtc(r.Datetime) ??
    parseUtc(r.dateStart) ??
    parseUtc(r.startDatetime) ??
    parseUtc(r.measuredAt)
  );
}

/**
 * Clip a fetched page to the sensor's current tenure.
 *
 * The boundary is INCLUSIVE: a row stamped exactly at the install belongs to
 * the new location.
 *
 * A row we cannot date is dropped when a cutoff is known -- it cannot be
 * attributed to a location, and admitting it would quietly re-import
 * old-location data -- but kept when there is no cutoff, where the whole
 * envelope is pass-through anyway.
 */
export function clipToTenure<T>(
  response: PagedResponse<T> | null | undefined,
  scope: TenureScope
): ScopedEnvelope<T> {
  const unclipped = Array.isArray(response?.data) ? (response as PagedResponse<T>).data! : [];
  const cutoff = scope.cutoffMs;
  const data =
    cutoff == null
      ? unclipped
      : unclipped.filter((row) => {
          const at = rowTimestampMs(row);
          return at !== null && at >= cutoff;
        });
  return {
    data,
    unclipped,
    scope,
    nextPageToken: response?.nextPageToken,
    size: response?.size,
  };
}

/**
 * The rows a reader should look at when the question is NOT "what happened at
 * this location": all of them, unclipped.
 *
 * Two callers today. The report hooks use it for the "online now" claim, which
 * a sensor installed an hour ago cannot answer from post-install rows alone --
 * reading the clipped array would report a healthy sensor as offline and raise
 * an equipment issue on a safety report. And the Location history section uses
 * it because looking across a move is the entire point of that section.
 *
 * Anything that aggregates into a figure describing "this sensor" must read
 * `.data` instead. That is the misattribution this module exists to stop.
 */
export function liveRows<T>(envelope: ScopedEnvelope<T> | undefined): T[] {
  return envelope?.unclipped ?? [];
}

/**
 * The scoped fetchers. `scope` is a REQUIRED parameter on every one of them, so
 * the compiler catches a caller that forgets it rather than silently handing a
 * consumer unclipped history.
 *
 * Rows are `unknown`: every downstream counter and bucketer already accepts
 * loose rows and tolerates missing fields, and narrowing here would only invent
 * a contract the API does not promise.
 */
export async function getScopedSensorAlarms(
  sensorName: string,
  pageSize: number,
  scope: TenureScope
): Promise<ScopedEnvelope<unknown>> {
  return clipToTenure<unknown>(await getSensorAlarms(sensorName, pageSize), scope);
}

export async function getScopedSensorSessions(
  sensorName: string,
  pageSize: number,
  dateTimeStart: string | null,
  dateTimeEnd: string | null,
  scope: TenureScope
): Promise<ScopedEnvelope<unknown>> {
  return clipToTenure<unknown>(
    await getSensorSessions(sensorName, pageSize, dateTimeStart, dateTimeEnd),
    scope
  );
}

export async function getScopedSensorConnectivity(
  sensorName: string,
  pageSize: number,
  scope: TenureScope
): Promise<ScopedEnvelope<unknown>> {
  return clipToTenure<unknown>(
    await getSensorConnectivity(sensorName, pageSize),
    scope
  );
}

export async function getScopedPCOStateChanges(
  sensorName: string,
  pageSize: number,
  dateTimeStart: string | null,
  dateTimeEnd: string | null,
  scope: TenureScope
): Promise<ScopedEnvelope<unknown>> {
  return clipToTenure<unknown>(
    await getPCOStateChanges(sensorName, pageSize, dateTimeStart, dateTimeEnd),
    scope
  );
}
